import { useAppSelector } from "hooks/storeHooks"
import { useLocation, Link as RouterLink } from "react-router-dom"
import { ScanFace, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  CATEGORY_ROUTES,
  isTryOnCategory,
  type TryOnCategoryKey,
} from "@/features/home/categoryLinks"
import { selectCategoriesLoading, selectSelectedCategories } from "store/product/productSlice"

interface NavSectionCategoriesProps {
  collapsed?: boolean
}

const CATEGORY_LABEL_KEYS: Record<TryOnCategoryKey, string> = {
  glasses: "productCategories.glasses",
  makeup: "productCategories.makeup",
}

export const NavSectionCategories = ({ collapsed = false }: NavSectionCategoriesProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const selectedCategories = useAppSelector(selectSelectedCategories)
  const categoriesLoading = useAppSelector(selectCategoriesLoading)

  const tryOnCategories = selectedCategories.filter(isTryOnCategory)
  const categoryItems = tryOnCategories.map((key) => ({
    key,
    label: t(CATEGORY_LABEL_KEYS[key]),
    to: CATEGORY_ROUTES[key].list,
  }))
  const currentCategoryPath = `/${location.pathname.split("/").filter(Boolean)[0] || ""}`
  const isProductsActive = categoryItems.some((item) => item.to === currentCategoryPath)
  const productsLink = {
    label: t("nav.products"),
    to: categoryItems[0]?.to ?? CATEGORY_ROUTES.makeup.list,
  }

  if (collapsed)
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <RouterLink
            to={productsLink.to}
            data-testid="nav-products"
            className={cn(
              "flex items-center justify-center rounded-md py-2 transition-colors",
              isProductsActive
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            )}
          >
            <ScanFace className="h-4 w-4" />
          </RouterLink>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {productsLink.label}
        </TooltipContent>
      </Tooltip>
    )

  return (
    <>
      <RouterLink
        to={productsLink.to}
        data-testid="nav-products"
        className={cn(
          "group flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[13px] transition-colors",
          isProductsActive
            ? "bg-accent text-foreground font-semibold"
            : "font-medium text-foreground/90 hover:bg-accent hover:text-foreground"
        )}
      >
        <span className="opacity-80 group-hover:opacity-100">
          <ScanFace className="h-4 w-4" />
        </span>
        <span className="truncate">{productsLink.label}</span>
      </RouterLink>

      {categoriesLoading ? (
        <div className="flex items-center justify-center py-2 pl-8">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground/50" />
        </div>
      ) : (
        categoryItems.map(({ key, label, to }) => (
          <RouterLink
            key={key}
            to={to}
            data-testid={`nav-category-${to.replace(/^\//, "")}`}
            className={cn(
              "flex items-center gap-2 rounded-md py-[6px] pl-9 pr-2 text-[13px] transition-colors",
              currentCategoryPath === to
                ? "text-foreground font-semibold"
                : "font-medium text-foreground/90 hover:text-foreground"
            )}
          >
            <span
              className={cn(
                "h-1 w-1 rounded-full",
                currentCategoryPath === to ? "bg-foreground" : "bg-foreground/55"
              )}
            />
            <span className="truncate">{label}</span>
          </RouterLink>
        ))
      )}
    </>
  )
}
